import * as React from 'react'
import styled from 'styled-components'
import { VIDEO_LEADERBOARD_QUERY } from '../../graphql/graphql-queries'
import { useInfiniteLoadIndexer } from '../../hooks/infiniteLoadIndexer'
import { VideoFullData } from '../../interfaces'
import { Mint, OrderBy, Query_Root, VideoLeaderboard } from '../../graphql/indexer-graphql-types'
import { fbEnrichUsersFromIds, fbEnrichVideosFromIds } from '../../data/FirebaseEnrich'
import { getMarketplaceData } from '../../data/IndexerUtils'
import TrendingVideoCard from '../video/card/TrendingVideoCard'
import { LeaderboardTime } from '.'
import { animated, useSpring } from 'react-spring'
import { useRouter } from 'next/router'
import { mintComparer } from '../../utils/comparer'

const itemsContainerStyle = `
  display: flex;
  flex-wrap: wrap;
  position: relative;
  z-index: 0;
  width: 100%;

  // large desktop
  @media (min-width: 82em) {
    max-width: 1800px;
  }
`

const StyledSkeletonContainer = styled.div`
  ${itemsContainerStyle}
`

const StyledInfiniteScroll = styled.div`
  ${itemsContainerStyle}
  :after {
    content: '';
    position: absolute;
    z-index: 1;
    pointer-events: none;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: -webkit-linear-gradient(
      270deg,
      rgba(16, 16, 16, 0),
      rgba(16, 16, 16, 0),
      rgba(16, 16, 16, 0),
      rgba(16, 16, 16, 0),
      rgba(16, 16, 16, 1)
    );

    @media (min-width: 43em) {
      background: -webkit-linear-gradient(
        270deg,
        rgba(16, 16, 16, 0),
        rgba(16, 16, 16, 0),
        rgba(16, 16, 16, 0),
        rgba(16, 16, 16, 0),
        rgba(16, 16, 16, 0.25),
        rgba(16, 16, 16, 0.75)
      );
    }
  }
`

const ViewMoreButton = styled(animated.div)`
  border-style: solid;
  border-width: 1px;
  border-color: #fff;
  position: absolute;
  z-index: 2;
  bottom: 0;
  left: 20%;
  right: 20%;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #101010;

  font-size: 14px;
  padding: 10px 0;
  width: calc(60% - 15px);
  border-radius: 10px;
  /* margin-right: 25px; */

  @media (min-width: 43em) {
    left: 30%;
    right: 30%;
    font-size: 18px;
    padding: 15px 0;
    width: calc(40% - 15px);
    border-radius: 15px;
    /* margin-right: 50px; */
  }
`

const parseItems = (data: Query_Root | undefined): VideoLeaderboard[] => {
  if (!data) {
    return []
  }
  return data.videoLeaderboard?.filter(Boolean).filter(({ mintObject }) => !!mintObject) as
    | VideoLeaderboard[]
    | []
}

const enrichItems = async (leaderboardItems: VideoLeaderboard[]): Promise<VideoFullData[]> => {
  const mints = leaderboardItems.map(({ mintObject }) => mintObject) as Mint[] | []

  if (!mints.length) {
    return []
  }

  const ids = mints.map((mint) => mint.id)
  const enrichedVideos = await fbEnrichVideosFromIds(ids)
  const creators = await fbEnrichUsersFromIds(enrichedVideos.map((video) => video.created_by))
  return enrichedVideos.map((video) => {
    const mint = mints.find((mint) => mint.id === video.address)
    const creator = creators.find((creator) => creator.address === video.created_by)
    const leaderboardData = leaderboardItems.find((data) => data.mint === video.address)
    const marketplaceData = mint ? getMarketplaceData(mint) : undefined

    return {
      video,
      creator,
      leaderboardInfo: leaderboardData
        ? {
            sevenDays: leaderboardData.sevenDays,
            thirtyDays: leaderboardData.thirtyDays,
            allTime: leaderboardData.allTime,
            sevenDayPrim: leaderboardData.sevenDayPrim,
            thirtyDayPrim: leaderboardData.thirtyDayPrim,
            allTimePrim: leaderboardData.allPrim,
            sevenDaySec: leaderboardData.sevenDaySec,
            thirtyDaySec: leaderboardData.thirtyDaySec,
            allTimeSec: leaderboardData.allSec,
          }
        : undefined,
      ...marketplaceData,
    }
  })
}

interface Props {
  time: LeaderboardTime
  currentIndex: number
  index: number
  updateHeight: () => void
}

const TrendingVideosHome = ({ currentIndex, index, updateHeight }: Props): JSX.Element => {
  const time = LeaderboardTime.ALL
  const {
    rawData: originalItems,
    data: items,
    isLoading,
    hasNext,
    refetch,
  } = useInfiniteLoadIndexer<VideoFullData, Mint>({
    id: '',
    query: VIDEO_LEADERBOARD_QUERY,
    enrichItems,
    parseItems,
    comparer: mintComparer,
    page_size: 6,
    refetchMarker: 'trendingVideos',
    variables: {
      orderBy:
        time === LeaderboardTime.ALL
          ? [{ allTime: OrderBy.Desc }]
          : time === LeaderboardTime.MONTH
          ? [{ thirtyDays: OrderBy.Desc }]
          : [{ sevenDays: OrderBy.Desc }],
    },
  })

  const [viewMoreHover, setViewMoreHover] = React.useState(false)
  const router = useRouter()

  const viewMoreHoverState = useSpring({
    boxShadow: viewMoreHover ? '0px 5px 25px rgba(255,255,255,0.1)' : '0px 0px 0px rgba(0,0,0,0)',
    transform: viewMoreHover ? `translate3d(0,-5%,0)` : `translate3d(0,0%,0)`,
    config: {
      mass: 1,
      tension: 170,
      friction: 26,
      velocity: 0,
    },
  })

  React.useEffect(() => {
    if (index === currentIndex) {
      refetch()
    }
  }, [currentIndex, index, time])

  React.useEffect(() => {
    if (index === currentIndex) {
      updateHeight()
    }
  }, [items])

  if ((originalItems.length === 0 && hasNext) || isLoading) {
    return (
      <StyledSkeletonContainer>
        {[...Array(6).keys()].map((index) => (
          <TrendingVideoCard time={time} rank={index + 1} key={`skeleton-item-${index}`} />
        ))}
      </StyledSkeletonContainer>
    )
  }

  if (items.length === 0) {
    return <></>
  }

  return (
    <StyledInfiniteScroll>
      {items.map(
        (item: VideoFullData, index: number): JSX.Element => (
          <TrendingVideoCard time={time} item={item} rank={index + 1} key={`list-item-${index}`} />
        )
      )}
      <ViewMoreButton
        onMouseEnter={() => setViewMoreHover(true)}
        onMouseLeave={() => setViewMoreHover(false)}
        onClick={() => router.push('/trending')}
        style={viewMoreHoverState}
      >
        {`View All Trending`}
      </ViewMoreButton>
    </StyledInfiniteScroll>
  )
}

export default TrendingVideosHome
